/**
 * Mathlive Answer Utilities
 *
 * Mathlive short_answer questions traditionally stored the correct answer as
 * a plain LaTeX string (e.g. `"\\(x+2\\)"`). To support questions whose
 * answer may also be prose like "no solutions" or "infinitely many", an
 * `answer_config.allowTextInput` flag was added. When enabled, answers are
 * serialized as JSON objects of shape `{ type: 'math' | 'text', value }`.
 *
 * Readers must handle BOTH shapes because the flag can be flipped on/off
 * without migrating previously-stored answers.
 */

export const defaultMathliveConfig = {
    allowTextInput: false,
    spec: null,
};

export const MATHLIVE_ANSWER_TYPES = Object.freeze({
    MATH: 'math',
    TEXT: 'text',
});

/**
 * Parse a single stored answer entry into `{ type, value }`.
 * - New shape: JSON-stringified `{ type, value }` → returned as-is
 * - Legacy shape: raw string → treated as a math answer
 */
export const parseMathliveAnswer = (raw) => {
    if (raw == null) return { type: MATHLIVE_ANSWER_TYPES.MATH, value: '' };
    if (typeof raw === 'object') {
        const type = raw.type === MATHLIVE_ANSWER_TYPES.TEXT
            ? MATHLIVE_ANSWER_TYPES.TEXT
            : MATHLIVE_ANSWER_TYPES.MATH;
        return { type, value: raw.value ?? '' };
    }
    const str = String(raw);
    if (str.startsWith('{')) {
        try {
            const parsed = JSON.parse(str);
            if (parsed && typeof parsed === 'object' && 'type' in parsed && 'value' in parsed) {
                const type = parsed.type === MATHLIVE_ANSWER_TYPES.TEXT
                    ? MATHLIVE_ANSWER_TYPES.TEXT
                    : MATHLIVE_ANSWER_TYPES.MATH;
                return { type, value: parsed.value ?? '' };
            }
        } catch (_err) { /* fall through to legacy string handling */ }
    }
    return { type: MATHLIVE_ANSWER_TYPES.MATH, value: str };
};

export const stringifyMathliveAnswer = ({ type, value }) => JSON.stringify({
    type: type === MATHLIVE_ANSWER_TYPES.TEXT
        ? MATHLIVE_ANSWER_TYPES.TEXT
        : MATHLIVE_ANSWER_TYPES.MATH,
    value: value ?? '',
});

/**
 * Normalize a math value for string comparison. Mirrors multiline's mathlive
 * normalization — text-based, not algebraic.
 */
const normalizeMathValue = (v) => String(v ?? '')
    .trim()
    .replace(/\\,/g, '')
    .replace(/\s+/g, '')
    .toLowerCase();

const normalizeTextValue = (v) => String(v ?? '').trim().toLowerCase();

export const mathValuesMatch = (a, b) => {
    const na = normalizeMathValue(a);
    const nb = normalizeMathValue(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    const fa = parseFloat(na);
    const fb = parseFloat(nb);
    if (!isNaN(fa) && !isNaN(fb)) return fa === fb;
    return false;
};

export const textValuesMatch = (a, b) => {
    const na = normalizeTextValue(a);
    const nb = normalizeTextValue(b);
    if (!na || !nb) return false;
    return na === nb;
};

/**
 * Wrap a raw mathlive string with `\( ... \)` LaTeX delimiters if not already.
 * Used both by the legacy path and by the math-typed new path.
 */
export const wrapLatexDelimiters = (value) => {
    if (!value) return value;
    const trimmed = String(value).trim();
    if (/^\\\(.*\\\)$/.test(trimmed) || /^\$.*\$$/.test(trimmed) || /^\\\[.*\\\]$/.test(trimmed)) {
        return value;
    }
    return `\\(${value}\\)`;
};

export const hasLatexDelimiters = (value) => /\\\(|\\\)|\$/.test(String(value ?? ''));
